import { Table, TableBody, TableCell, TablePagination, TableRow } from '@material-ui/core';
import React, { FC, useCallback } from 'react';
import { createUseStyles } from 'src/theming';
import Request, { getLabelForRequestType } from 'src/interfaces/api/Request';
import moment from 'moment';
import RequestTableHead from './components/RequestTableHead';
import { Order } from 'src/interfaces/Table';
import Site from 'src/interfaces/api/Site';
import Contact from 'src/interfaces/api/Contact';
import { useRecoilValue } from 'recoil';
import { unreadNotificationsByLinkTypeState } from 'src/recoil/selectors/notifications';
import { LinkType } from 'src/interfaces/Notification';
import clsx from 'clsx';

const useStyles = createUseStyles({
  root: {
    overflowX: 'auto',
  },
  table: {
    minWidth: 650,
  },
  contact: {
    whiteSpace: 'nowrap',
  },
  subInfo: {
    fontSize: 11,
    color: '#0000008f',
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  row: {
    cursor: 'pointer',
  },
  unread: {
    fontWeight: 'bold !important',
  },
});

interface Props {
  requests: Request[];
  sites: { [key: string]: Site };
  contacts: Contact[];
  onClickRequest: (requestId: string) => void;
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  onChangePage: (event: unknown, newPage: number) => void;
  onChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  page: number;
  rowsPerPage: number;
  order: Order;
  orderBy: string;
  total: number;
}

const RequestTable: FC<Props> = ({
  requests,
  sites,
  contacts,
  onClickRequest,
  onChangeRowsPerPage,
  onChangePage,
  onRequestSort,
  rowsPerPage,
  total,
  page,
  order,
  orderBy,
}) => {
  const classes = useStyles();
  const unreadRequestNotifications = useRecoilValue(unreadNotificationsByLinkTypeState(LinkType.request));

  const handleClickRow = (requestId: string) => {
    onClickRequest(requestId);
  };

  const getContact = useCallback(
    (contactId: string) => contacts.find((contact) => contact.customerAccessUserId === contactId),
    [requests, contacts]
  );

  const isRequestUnread = useCallback(
    (request: Request) => {
      return -1 !== unreadRequestNotifications.findIndex((notification) => notification.linkId === request.id);
    },
    [unreadRequestNotifications]
  );

  return (
    <>
      <div className={classes.root}>
        <Table className={classes.table}>
          <RequestTableHead order={order} orderBy={orderBy} onRequestSort={onRequestSort} />

          <TableBody>
            {requests.length <= 0 ? (
              <TableRow>
                <TableCell colSpan={7}>Aucune demande trouvée ...</TableCell>
              </TableRow>
            ) : (
              requests.map((request) => {
                const creator = getContact(request.creatorId);
                const site = sites[request.siteId];

                return (
                  <TableRow className={classes.row} key={request.id} onClick={() => handleClickRow(request.id)} hover>
                    <TableCell
                      component='th'
                      scope='row'
                      className={clsx({ [classes.unread]: isRequestUnread(request) })}
                    >
                      {request.id}
                    </TableCell>
                    <TableCell align='right'>{request.subject}</TableCell>
                    <TableCell align='right'>{getLabelForRequestType(request.type)}</TableCell>
                    <TableCell align='right'>
                      <div className={classes.contact}>
                        {`${creator?.firstname || '---'} ${creator?.lastname || '---'}`}
                      </div>

                      <div className={classes.subInfo}>
                        <b>site: </b>
                        {site?.socialReason || '---'}
                      </div>
                    </TableCell>
                    <TableCell align='right'>{moment(request.createdAt).format('DD/MM/YYYY')}</TableCell>
                    <TableCell align='right'>
                      {request.updatedAt ? moment(request.updatedAt).format('DD/MM/YYYY') : '--/--/--'}
                    </TableCell>
                    <TableCell align='right'>{request.status}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
        labelRowsPerPage='Demandes par page: '
        component='div'
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
      />
    </>
  );
};

export default React.memo(RequestTable);
